import { Injectable } from '@angular/core';
import { EventHistoryFilterRequirements, PolicyEffects, productCodes } from '../constants/constants';
import { Account } from '@mitel/cloudlink-sdk';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { AppStateService } from './app-state.service';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { CAPFeaturePolicyTestRequest } from '../constants/productPolicies';

@Injectable({
    providedIn: 'root'
})

export class GuardPermissionsService {
    
    constructor(
        private adminService: AdminService,
        private accService: AccountsService,
        private appStateService: AppStateService,
        private authService: AuthService,
    ) { }

    /*
        This method will determined which requirements have been satisfied to access Event History.
        Currently you need either MiVB+CAP or ZAPPA.
        Only to be called after the account-admin.guard has returned true.
        This ensures the current account has been set in accService.
        Also Event History is only allowed with the Account_Admin role
    */
    async determineSatisfiedRequirementsForEventHistory(route: Router): Promise<EventHistoryFilterRequirements []>{
        let mivbCapExists: boolean = false;
        let zappaExists: boolean = false;
        let account: Account;

        const supportMode = this.appStateService.getSupportPortalMode();
        if(!supportMode){
            try {
                account = this.accService.getCurrentAccount();
                if(account){
                    let satisfiedRequirements: EventHistoryFilterRequirements [] = [];

                    //If Event History is being accessed as a result of a menu click - the menu service already determined the
                    //satisfied requirements and sent them along in the state - saves us work. Just return those.
                    let currentNavDetails = route.getCurrentNavigation();
                    satisfiedRequirements = currentNavDetails?.extras?.state?.satisfiedRequirements &&  currentNavDetails.extras.state.satisfiedRequirements.length > 0 ? currentNavDetails.extras.state.satisfiedRequirements : [];

                    if(satisfiedRequirements.length > 0){
                        return satisfiedRequirements;
                    }

                    //If Event History is being accessed by some other way (eg. company switcher, refresh, manually typing url), 
                    //then we need to determine which requirements have been satisfied.

                    let tags = await this.adminService.getAccountTags(account.accountId);
                    mivbCapExists = await this.doesAccountHaveMivbCAP(tags);
                    zappaExists = this.doesAccountHaveZappa(tags);

                    if(mivbCapExists){
                        satisfiedRequirements.push(EventHistoryFilterRequirements.MIVB_CAP);
                    }
                    if(zappaExists){
                        satisfiedRequirements.push(EventHistoryFilterRequirements.ZAPPA);
                    }
                    return satisfiedRequirements;
                }
                else{
                    throw 'no account'
                }
    
            } catch (error) {
                console.error("Error determining access to Event History: ", error);
                return [];
            }
        }
        else {
            console.error("Event History access not permitted in Support Portal mode");
            return [];
        }
    }

    /*
        Determine if the account has CAP enabled.
        If the account does not have the MiVB tags, don't bother running the policy check for CAP
    */
    async doesAccountHaveMivbCAP(tags: any): Promise<boolean> {
        let integrationAdded = tags && tags.products && tags.products.length > 0 && tags.products.includes(productCodes.MiVB_INTEGRATION) ? true : false;
        if(integrationAdded){
            let capEnabled = await this.isCAPFeatureEnabled();
            if(capEnabled){
                return true;
            }
        }
        return false;
    }

    /*
        Would have preferred to use isCAPFeatureEnabled from IntegCustomerAdminPortalService but using 
        the service was causing issues with unit tests.
    */
    async isCAPFeatureEnabled(): Promise<boolean> {
        try {
            let isCapAllowed = await this.authService.doesPolicyForFeatureHaveDesiredEffect(CAPFeaturePolicyTestRequest, PolicyEffects.ExplicitAllow);
             return isCapAllowed;
        } catch (error) {
            console.log("CAP policy test failed", error);
			return false;
        }
    }

    /*
        Inspiration taken from getSetupCompleteValue() of integ-zappa-phone.service.ts.
        But that method also does a getTenantByType if no zappa tags are present.
        Opting not to do getTenantByType here as the fallback to the account not having the tags.
        Should be unlikely that the account has a Zappa tenant but the Zappa tag is not there on a refresh or manual type of url or a company switch.
        The account would need to be in a bad state with tags or have never been loaded before in Accounts app - because at least the menuService would
        have called getLeftNavMenus() --> getSetupCompleteValue() --> getTenantByType()
        Seems inappropriate to do getTenantByType and then try to fix zappa tags in a guard check.
    */
    doesAccountHaveZappa(tags: any): boolean {

        if ( tags && tags.Zappa && tags.Zappa.setupComplete == true ){
            return true;
        }
        return false;
    }
}